import { MultiColumnDropdownWithLabel } from '@blueprint/multicolumn-dropdown';
import { Radio } from '@blueprint/ui';
import {
    propertyTypeSelector,
    updateFilter
} from '@zoopla/redux-reducer-filters';
import {
    FilterOptions,
    PropertyTypes
} from '@zoopla/redux-types/filters.types';
import { ChangeEvent, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

type RadioOptions = {
    options: FilterOptions;
    onPropertyTypeChange: (evt: ChangeEvent<HTMLInputElement>) => void;
    propertyType: string | string[];
};

const PropertyTypeRadioButtonWrapper = styled.div(
    () => css`
        display: flex;
        flex-wrap: wrap;
    `
);

const options: { [key: string]: string } = {
    '': 'Show all',
    all: 'All property types',
    houses: 'Houses',
    farms_land: 'Farms/land',
    flats: 'Flats'
};

const PropertyTypeRadioButtons: FC<RadioOptions> = ({
    options,
    onPropertyTypeChange,
    propertyType
}) => {
    return (
        // see: https://github.com/yannickcr/eslint-plugin-react/issues/2584
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
            {options?.map((option) => {
                const { label, value } = option;

                const idKey = `desktop_subheader_property_type_option${
                    value ? `_${value}` : '_all'
                }`;

                const hasSelectedThisPropertyType = propertyType === value;

                return (
                    <Radio
                        key={`${value}-${label}`}
                        id={idKey}
                        data-testid={value}
                        name="option"
                        label={label}
                        value={value}
                        checked={hasSelectedThisPropertyType}
                        onChange={onPropertyTypeChange}
                    />
                );
            }) ?? []}
        </>
    );
};

export const SelectPropertyType: FC<{
    excludePropertyTypes?: PropertyTypes;
}> = ({ excludePropertyTypes = [] }) => {
    const reduxDispatch = useDispatch();
    const {
        selectedValue: propertyTypeSelectedValue,
        options: propertyTypeOptions
    } = useSelector(propertyTypeSelector);

    const filteredPropertyTypeOptions = propertyTypeOptions.filter(
        (propertyTypeOption) =>
            !excludePropertyTypes.includes(propertyTypeOption.value as never)
    );

    const onPropertyTypeChange = (evt: ChangeEvent<HTMLInputElement>) => {
        reduxDispatch(updateFilter('propertyType', evt.currentTarget.value));
    };

    return (
        <MultiColumnDropdownWithLabel
            title={options[propertyTypeSelectedValue]}
            label="Property type"
            testId="PropertyType_testId"
            isLeft
            minWidthOverride="100%"
        >
            <PropertyTypeRadioButtonWrapper>
                <PropertyTypeRadioButtons
                    onPropertyTypeChange={onPropertyTypeChange}
                    propertyType={propertyTypeSelectedValue}
                    options={filteredPropertyTypeOptions}
                />
            </PropertyTypeRadioButtonWrapper>
        </MultiColumnDropdownWithLabel>
    );
};
